/**
 * AttachmentPanel — file attachment UI for catalog/document records.
 *
 * Sources:
 *   - 💻 Local: drag & drop + click to browse
 *   - 🔗 URL: paste link to any file
 *   - ☁️ Cloud: Google Drive / OneDrive / Dropbox (via picker SDKs, configurable)
 */
import React, { useEffect, useState, useCallback, useRef } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Chip from '@mui/material/Chip'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Collapse from '@mui/material/Collapse'
import TextField from '@mui/material/TextField'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemDecorator from '@mui/material/ListItemIcon'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import ImageIcon from '@mui/icons-material/Image'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import LinkIcon from '@mui/icons-material/Link'
import AddIcon from '@mui/icons-material/Add'
import ComputerIcon from '@mui/icons-material/Computer'
import CloudIcon from '@mui/icons-material/Cloud'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { ApiService } from '@/utils/api'
import apiClient from '@/utils/api'
import { useAttachmentUpload } from './useAttachmentUpload'
import { FillFromFileDialog, ExtractionProposal } from './FillFromFileDialog'
import type { IntakeField, IntakeTabularPart } from './ScanImportDialog'
import { SAFE_PREVIEW_IMAGE_TYPES, canPreviewInline, PDF_MAGIC, blobStartsWith } from './print/filePreview'

export interface Attachment {
  id: string
  file_name: string
  file_size: number
  content_type: string
  storage_type: string
  description: string
  ocr_text?: string
  ocr_status?: string
  created_at: string
}

interface AttachmentPanelProps {
  connectionId: string
  ownerType: string
  ownerName: string
  ownerId: string
  /** When true, renders without the collapsible header/border (used inside a tab). */
  embedded?: boolean
  /** Called when attachment count changes (for parent badge/label). */
  onCountChange?: (count: number) => void
  /** When changed, triggers reload of attachments (used by parent to force refresh). */
  reloadKey?: number
  /** Current form values — used to pre-check empty fields in the FillFromFileDialog. */
  currentData?: Record<string, unknown>
  /** When provided, enables "Fill fields from file" on scannable attachments. */
  onApplyExtraction?: (header: Record<string, unknown>, tabularAppend: Record<string, Record<string, unknown>[]>) => void
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function getFileIcon(contentType: string) {
  if (contentType.startsWith('image/')) return <ImageIcon color="primary" />
  if (contentType === 'application/pdf') return <PictureAsPdfIcon color="error" />
  if (contentType.startsWith('text/')) return <TextSnippetIcon color="info" />
  if (contentType === 'link') return <LinkIcon color="action" />
  return <InsertDriveFileIcon color="action" />
}

function isScannable(contentType: string): boolean {
  return contentType.startsWith('image/') || contentType === 'application/pdf'
}

const PREVIEW_PANEL_MIN_HEIGHT = 240
const LIST_WIDTH_DEFAULT = 220
const LIST_WIDTH_MIN = 70
const LIST_WIDTH_MAX = 560

const ZOOM_MIN = 0.25
const ZOOM_MAX = 3
const ZOOM_STEP = 0.25

// Gives the previewed content MORE layout room than the visible box (at
// zoom<1) and then visually shrinks the whole thing back with a CSS
// transform — a plain transform:scale() on a box that's already fixed at
// 100% only shrinks whatever was already decided (a PDF plugin's own crop/
// scroll stays put, just smaller); this way the plugin actually gets more
// room to render into, so "zoom out" genuinely reveals more of the page
// instead of shrinking the same partial view. At zoom>1 the same formula
// gives it LESS room, then blows the result back up — the standard way to
// make content overflow (and scroll, via the ancestor's overflow:auto).
function ZoomWrapper({ zoom, children }: { zoom: number; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        width: `${100 / zoom}%`,
        height: `${100 / zoom}%`,
        transform: `scale(${zoom})`,
        transformOrigin: 'center center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  )
}

export const AttachmentPanel: React.FC<AttachmentPanelProps> = ({
  connectionId,
  ownerType,
  ownerName,
  ownerId,
  embedded,
  onCountChange,
  reloadKey,
  currentData,
  onApplyExtraction,
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState(true)
  const [ocrTextId, setOcrTextId] = useState<string | null>(null)
  const [scanning, setScanning] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [fillProposal, setFillProposal] = useState<ExtractionProposal | null>(null)
  const [fillOpen, setFillOpen] = useState(false)
  const [extracting, setExtracting] = useState<string | null>(null)
  const [fillFields, setFillFields] = useState<IntakeField[]>([])
  const [fillParts, setFillParts] = useState<IntakeTabularPart[]>([])
  const [selectedAtt, setSelectedAtt] = useState<Attachment | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewText, setPreviewText] = useState<string | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [zoom, setZoom] = useState(1)
  const [listWidth, setListWidth] = useState(LIST_WIDTH_DEFAULT)
  const [isResizingList, setIsResizingList] = useState(false)
  const splitRef = useRef<HTMLDivElement>(null)

  // Add menu
  const [addMenuAnchor, setAddMenuAnchor] = useState<HTMLElement | null>(null)
  // Link dialog
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkName, setLinkName] = useState('')
  // Cloud settings
  const [cloudSettingsOpen, setCloudSettingsOpen] = useState(false)
  const [cloudSettings, setCloudSettings] = useState<import('./cloudPickers').CloudSettings>({})
  useEffect(() => {
    import('./cloudPickers').then(m => m.loadCloudSettings(connectionId)).then(setCloudSettings).catch(() => {})
  }, [connectionId])

  // Attachment upload hook
  const { uploadFiles, uploading: hookUploading, error: uploadError, clearError } = useAttachmentUpload({
    connectionId,
    ownerType,
    ownerName,
    ownerId,
  })

  const qs = `connection_id=${encodeURIComponent(connectionId)}&owner_type=${encodeURIComponent(ownerType)}&owner_name=${encodeURIComponent(ownerName)}&owner_id=${encodeURIComponent(ownerId)}`

  const loadAttachments = useCallback(async () => {
    setLoading(true)
    try {
      const data = await ApiService.get<Attachment[]>(`/v1/attachments/list?${qs}`)
      setAttachments(data)
      onCountChange?.(data.length)
    } catch { /* ignore */ }
    setLoading(false)
  }, [qs, onCountChange])

  useEffect(() => {
    if (ownerId) loadAttachments()
  }, [ownerId, reloadKey, loadAttachments])

  const handleUpload = useCallback(async (files: FileList | File[]) => {
    await uploadFiles(files)
    loadAttachments()
  }, [uploadFiles, loadAttachments])

  const handleCloudFile = useCallback(async (file: import('./cloudPickers').CloudFile | null) => {
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const linkBlob = new Blob([file.url], { type: 'text/uri-list' })
      const formData = new FormData()
      formData.append('file', linkBlob, file.name)
      await apiClient.post(
        `/v1/attachments/upload?${qs}&description=${encodeURIComponent(file.url)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
    } catch (e: any) {
      setError(e?.message || 'Failed to add cloud file')
    }
    setUploading(false)
    loadAttachments()
  }, [qs, loadAttachments])

  const handleAddLink = useCallback(async () => {
    if (!linkUrl.trim()) return
    setUploading(true)
    setError(null)
    try {
      // Create a link attachment (stored as metadata only, no file data)
      const fileName = linkName.trim() || linkUrl.split('/').pop() || 'Link'
      const linkBlob = new Blob([linkUrl], { type: 'text/uri-list' })
      const formData = new FormData()
      formData.append('file', linkBlob, fileName)
      await apiClient.post(`/v1/attachments/upload?${qs}&description=${encodeURIComponent(linkUrl)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    } catch (e: any) {
      setError(e?.message || 'Failed to add link')
    }
    setUploading(false)
    setLinkDialogOpen(false)
    setLinkUrl('')
    setLinkName('')
    loadAttachments()
  }, [linkUrl, linkName, qs, loadAttachments])

  const handleDelete = useCallback(async (id: string) => {
    try {
      await ApiService.delete(`/v1/attachments/${id}?connection_id=${encodeURIComponent(connectionId)}`)
      loadAttachments()
    } catch { /* ignore */ }
  }, [connectionId, loadAttachments])

  // Link attachments store a URL in `description`. Only ever follow http(s):
  // the field is free text, and `javascript:` would run on our own origin.
  const linkHref = useCallback((att: Attachment): string | null => {
    if (!att.description) return null
    let scheme = ''
    try {
      scheme = new URL(att.description).protocol
    } catch {
      scheme = ''
    }
    return scheme === 'http:' || scheme === 'https:' ? att.description : null
  }, [])

  // Fetched rather than opened in a tab: the download/view endpoints require
  // authentication, and a plain navigation carries no Authorization header.
  const fetchBlob = useCallback(async (att: Attachment): Promise<Blob> => {
    const res = await apiClient.get(
      `/v1/attachments/download/${att.id}?connection_id=${encodeURIComponent(connectionId)}`,
      { responseType: 'blob' },
    )
    return res.data as Blob
  }, [connectionId])

  const handleDownload = useCallback(async (att: Attachment) => {
    const href = linkHref(att)
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer')
      return
    }
    if (att.description?.startsWith('http')) return

    try {
      const blob = await fetchBlob(att)
      const blobHref = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobHref
      link.download = att.file_name || 'attachment'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(blobHref)
    } catch (e) {
      setError((e as Error)?.message || 'Failed to download attachment')
    }
  }, [linkHref, fetchBlob])

  const handleOpen = useCallback(async (att: Attachment) => {
    const href = linkHref(att)
    if (href) {
      window.open(href, '_blank', 'noopener,noreferrer')
      return
    }
    if (!canPreviewInline(att.content_type)) {
      // Anything outside the safe-render allowlist would navigate a
      // same-origin blob: tab to unreviewed content — download it instead.
      await handleDownload(att)
      return
    }
    // window.open() only counts as a user-gesture popup when it runs
    // SYNCHRONOUSLY inside the click handler — the blob isn't fetched yet
    // at this point, so open a blank tab now (still inside the gesture)
    // and point it at the blob once the fetch resolves. Doing it the other
    // way around (fetch, then window.open) is silently popup-blocked by
    // the time the await returns, most aggressively in Safari.
    const newTab = window.open('', '_blank')
    if (newTab) newTab.opener = null // noopener-equivalent without losing the handle
    try {
      const blob = await fetchBlob(att)
      if (att.content_type === 'application/pdf' && !(await blobStartsWith(blob, PDF_MAGIC))) {
        // Named *.pdf but isn't one — don't navigate a same-origin tab to
        // unreviewed bytes just because the extension said it's safe.
        newTab?.close()
        await handleDownload(att)
        return
      }
      const blobHref = URL.createObjectURL(blob)
      if (newTab) {
        newTab.location.href = blobHref
      } else {
        // The blank tab itself got blocked (strict popup-blocker settings) —
        // try a direct open anyway; nothing left to lose at this point.
        window.open(blobHref, '_blank', 'noopener,noreferrer')
      }
      // Revoked later, not immediately: the new tab loads the blob async.
      setTimeout(() => URL.revokeObjectURL(blobHref), 60_000)
    } catch (e) {
      newTab?.close()
      setError((e as Error)?.message || 'Failed to open attachment')
    }
  }, [linkHref, fetchBlob, handleDownload])

  // Selecting a row drives the embedded preview pane — fetches the blob only
  // for types the pane can actually render; other types (and links) just
  // switch the pane's "no preview" / link state, no network call. text/plain
  // is read as a string and rendered as plain React text (never HTML/an
  // iframe — inert no matter what the bytes are); application/pdf is magic-
  // number-checked before it's ever handed to the native PDF renderer.
  const handleSelect = useCallback(async (att: Attachment) => {
    setSelectedAtt(att)
    setPreviewUrl(null)
    setPreviewText(null)
    setPreviewError(null)
    setZoom(1)
    if (linkHref(att) || !canPreviewInline(att.content_type)) return
    setPreviewLoading(true)
    try {
      const blob = await fetchBlob(att)
      if (att.content_type === 'text/plain') {
        setPreviewText(await blob.text())
      } else if (att.content_type === 'application/pdf') {
        if (await blobStartsWith(blob, PDF_MAGIC)) {
          setPreviewUrl(URL.createObjectURL(blob))
        } else {
          setPreviewError("This file is named .pdf but its content isn't a valid PDF.")
        }
      } else {
        setPreviewUrl(URL.createObjectURL(blob))
      }
    } catch (e) {
      setPreviewError((e as Error)?.message || 'Failed to load preview')
    }
    setPreviewLoading(false)
  }, [linkHref, fetchBlob])

  const zoomOut = useCallback(() => {
    setZoom((z) => Math.max(ZOOM_MIN, Math.round((z - ZOOM_STEP) * 100) / 100))
  }, [])
  const zoomIn = useCallback(() => {
    setZoom((z) => Math.min(ZOOM_MAX, Math.round((z + ZOOM_STEP) * 100) / 100))
  }, [])
  const zoomReset = useCallback(() => setZoom(1), [])

  const handleListResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    setIsResizingList(true)
  }, [])

  // Tracks the drag as a document-level listener (not on the divider itself)
  // so the resize keeps following the cursor even when it moves off the
  // 6px-wide handle mid-drag. Width is computed from the split container's
  // own left edge each move, not an accumulated delta, so it can't drift.
  useEffect(() => {
    if (!isResizingList) return
    const onMove = (e: MouseEvent) => {
      const rect = splitRef.current?.getBoundingClientRect()
      if (!rect) return
      // Leaves at least 100px for the preview pane regardless of LIST_WIDTH_MAX,
      // so a narrow form can't have the divider dragged into starving it.
      const max = Math.max(LIST_WIDTH_MIN, Math.min(LIST_WIDTH_MAX, rect.width - 100))
      setListWidth(Math.min(max, Math.max(LIST_WIDTH_MIN, e.clientX - rect.left)))
    }
    const onUp = () => setIsResizingList(false)
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    const prevCursor = document.body.style.cursor
    const prevUserSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      document.body.style.cursor = prevCursor
      document.body.style.userSelect = prevUserSelect
    }
  }, [isResizingList])

  // One blob URL is ever "live" at a time (previewUrl) — revoke whichever
  // one is about to be replaced or the component unmounts holding it.
  useEffect(() => {
    if (!previewUrl) return
    return () => URL.revokeObjectURL(previewUrl)
  }, [previewUrl])

  // A delete (or a reload that drops the record) can remove the selected
  // attachment out from under the preview pane — fall back to empty state.
  useEffect(() => {
    if (selectedAtt && !attachments.some((a) => a.id === selectedAtt.id)) {
      setSelectedAtt(null)
      setPreviewUrl(null)
      setPreviewText(null)
      setPreviewError(null)
    }
  }, [attachments, selectedAtt])

  const handleOcr = useCallback(async (id: string) => {
    setScanning(id)
    try {
      await ApiService.post(`/v1/attachments/${id}/ocr?connection_id=${encodeURIComponent(connectionId)}`, {})
      loadAttachments()
    } catch { /* ignore */ }
    setScanning(null)
  }, [connectionId, loadAttachments])

  const handleFillFromFile = useCallback(async (attId: string) => {
    setExtracting(attId)
    setError(null)
    try {
      const meta = await ApiService.get<{ fields: IntakeField[]; tabular_parts: IntakeTabularPart[] }>(
        `/v1/import/${encodeURIComponent(connectionId)}/${encodeURIComponent(ownerName)}/fields`,
      )
      setFillFields(meta.fields)
      setFillParts(meta.tabular_parts || [])
      const resp = await apiClient.post(
        `/v1/import/${encodeURIComponent(connectionId)}/${encodeURIComponent(ownerName)}/extract-from-attachment`,
        { attachment_id: attId },
      )
      setFillProposal(resp.data as ExtractionProposal)
      setFillOpen(true)
      loadAttachments() // OCR may have been run and stored on the attachment
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Extraction failed')
    }
    setExtracting(null)
  }, [connectionId, ownerName, loadAttachments])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files)
    }
  }, [handleUpload])

  if (!ownerId) return null

  const bodyContent = (
    <>
      {(error || uploadError) && <Alert severity="error" sx={{ m: 1 }} onClose={() => { setError(null); clearError() }}>{error || uploadError}</Alert>}

      {/* Add button with source menu */}
        <Box sx={{ display: 'flex', gap: 1, m: 1, alignItems: 'center', flexShrink: 0 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={(e) => setAddMenuAnchor(e.currentTarget)}
          >
            Add
          </Button>
          <Menu
            anchorEl={addMenuAnchor}
            open={Boolean(addMenuAnchor)}
            onClose={() => setAddMenuAnchor(null)}
          >
            <MenuItem onClick={() => { setAddMenuAnchor(null); fileInputRef.current?.click() }}>
              <ListItemDecorator><ComputerIcon fontSize="small" /></ListItemDecorator>
              From computer
            </MenuItem>
            <MenuItem onClick={() => { setAddMenuAnchor(null); setLinkDialogOpen(true) }}>
              <ListItemDecorator><LinkIcon fontSize="small" /></ListItemDecorator>
              Link (URL)
            </MenuItem>
            {cloudSettings.google_picker_api_key && cloudSettings.google_picker_client_id && (
              <MenuItem onClick={async () => {
                setAddMenuAnchor(null)
                try {
                  const { openGoogleDrivePicker } = await import('./cloudPickers')
                  const file = await openGoogleDrivePicker(cloudSettings)
                  handleCloudFile(file)
                } catch (e: any) { setError(e?.message || 'Google Drive error') }
              }}>
                <ListItemDecorator><CloudIcon fontSize="small" /></ListItemDecorator>
                Google Drive
              </MenuItem>
            )}
            {cloudSettings.onedrive_client_id && (
              <MenuItem onClick={async () => {
                setAddMenuAnchor(null)
                try {
                  const { openOneDrivePicker } = await import('./cloudPickers')
                  const file = await openOneDrivePicker(cloudSettings)
                  handleCloudFile(file)
                } catch (e: any) { setError(e?.message || 'OneDrive error') }
              }}>
                <ListItemDecorator><CloudIcon fontSize="small" /></ListItemDecorator>
                OneDrive
              </MenuItem>
            )}
            {cloudSettings.dropbox_app_key && (
              <MenuItem onClick={async () => {
                setAddMenuAnchor(null)
                try {
                  const { openDropboxChooser } = await import('./cloudPickers')
                  const file = await openDropboxChooser(cloudSettings)
                  handleCloudFile(file)
                } catch (e: any) { setError(e?.message || 'Dropbox error') }
              }}>
                <ListItemDecorator><CloudIcon fontSize="small" /></ListItemDecorator>
                Dropbox
              </MenuItem>
            )}
            <MenuItem sx={{ borderTop: 1, borderColor: 'divider', mt: 0.5, pt: 0.5 }} onClick={() => { setAddMenuAnchor(null); setCloudSettingsOpen(true) }}>
              <ListItemDecorator><CloudIcon fontSize="small" color="action" /></ListItemDecorator>
              <Typography variant="body2" color="text.secondary">Configure cloud...</Typography>
            </MenuItem>
          </Menu>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => e.target.files && handleUpload(e.target.files)}
          />
          {(uploading || hookUploading) && <CircularProgress size={16} />}
        </Box>

        {/* Drop zone */}
        <Box
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          sx={{
            mx: 1, mb: 1, p: 1.5, border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'divider',
            borderRadius: 1,
            bgcolor: dragOver ? 'action.selected' : 'transparent',
            textAlign: 'center', cursor: 'pointer',
            transition: 'all 0.2s',
            flexShrink: 0,
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <CloudUploadIcon sx={{ fontSize: 24, color: 'text.secondary' }} />
          <Typography variant="caption" color="text.secondary" display="block">
            Drop files here
          </Typography>
        </Box>

        {/* File list + embedded preview pane — selecting a row previews it
            right there, no separate window (a modal preview is the wrong
            shape for "pick a file, look at it, pick another one"). Grows to
            fill whatever height the Files tab gives it (RuntimeFormRenderer
            stretches that tab's container the same way the General tab
            stretches its own form body). */}
        {attachments.length > 0 && (
          <Box
            ref={splitRef}
            sx={{
              display: 'flex',
              flex: 1,
              minHeight: PREVIEW_PANEL_MIN_HEIGHT,
              mx: 1, mb: 1,
              border: 1, borderColor: 'divider', borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ width: listWidth, flexShrink: 0, overflowY: 'auto' }}>
              <List dense disablePadding>
                {attachments.map((att) => (
                  <ListItemButton
                    key={att.id}
                    selected={selectedAtt?.id === att.id}
                    onClick={() => handleSelect(att)}
                    divider
                    sx={{ py: 0.5, px: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {att.description?.startsWith('http') ? <LinkIcon color="primary" fontSize="small" /> : getFileIcon(att.content_type)}
                    </ListItemIcon>
                    <ListItemText
                      primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                      secondaryTypographyProps={{ noWrap: true, fontSize: 11, component: 'span' }}
                      primary={att.file_name}
                      secondary={
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, maxWidth: '100%' }}>
                          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {att.description?.startsWith('http') ? att.description : formatFileSize(att.file_size)}
                          </Box>
                          {att.ocr_status === 'completed' && (
                            <Chip
                              component="span"
                              label="OCR"
                              size="small"
                              color="success"
                              sx={{ height: 14, fontSize: 9, flexShrink: 0 }}
                              onClick={(e) => { e.stopPropagation(); setOcrTextId(ocrTextId === att.id ? null : att.id) }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            <Box
              onMouseDown={handleListResizeStart}
              sx={{
                width: '6px',
                flexShrink: 0,
                cursor: 'col-resize',
                bgcolor: isResizingList ? 'primary.main' : 'divider',
                '&:hover': { bgcolor: 'primary.main' },
              }}
            />

            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
              {!selectedAtt ? (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">Select a file to preview</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                    {selectedAtt.description?.startsWith('http') ? <LinkIcon color="primary" fontSize="small" /> : getFileIcon(selectedAtt.content_type)}
                    <Typography variant="body2" noWrap title={selectedAtt.file_name} sx={{ flex: 1 }}>
                      {selectedAtt.file_name}
                    </Typography>
                    {onApplyExtraction && !selectedAtt.description?.startsWith('http') && isScannable(selectedAtt.content_type) && (
                      <Tooltip title="Fill fields from file">
                        <IconButton size="small" onClick={() => handleFillFromFile(selectedAtt.id)} disabled={extracting === selectedAtt.id}>
                          {extracting === selectedAtt.id ? <CircularProgress size={14} /> : <AutoFixHighIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {isScannable(selectedAtt.content_type) && selectedAtt.ocr_status !== 'completed' && (
                      <Tooltip title="OCR Scan">
                        <IconButton size="small" onClick={() => handleOcr(selectedAtt.id)} disabled={scanning === selectedAtt.id}>
                          {scanning === selectedAtt.id ? <CircularProgress size={14} /> : <DocumentScannerIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {selectedAtt.description?.startsWith('http') ? (
                      <Tooltip title="Open link">
                        <IconButton size="small" onClick={() => window.open(selectedAtt.description, '_blank')}>
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title="Open in new tab">
                          <IconButton size="small" onClick={() => handleOpen(selectedAtt)}>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download">
                          <IconButton size="small" onClick={() => handleDownload(selectedAtt)}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(selectedAtt.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'action.hover' }}>
                    {selectedAtt.description?.startsWith('http') ? (
                      <Box sx={{ p: 3, textAlign: 'center' }}>
                        <LinkIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>{selectedAtt.description}</Typography>
                      </Box>
                    ) : previewLoading ? (
                      <CircularProgress />
                    ) : previewError ? (
                      <Typography variant="body2" color="error" sx={{ p: 3 }}>{previewError}</Typography>
                    ) : previewText !== null ? (
                      <ZoomWrapper zoom={zoom}>
                        <Box
                          component="pre"
                          sx={{
                            m: 0, p: 2, width: '100%', height: '100%', overflow: 'auto',
                            fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                            bgcolor: 'background.paper', color: 'text.primary',
                          }}
                        >
                          {previewText}
                        </Box>
                      </ZoomWrapper>
                    ) : previewUrl && SAFE_PREVIEW_IMAGE_TYPES.has(selectedAtt.content_type) ? (
                      <ZoomWrapper zoom={zoom}>
                        <Box
                          component="img"
                          src={previewUrl}
                          alt={selectedAtt.file_name}
                          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                        />
                      </ZoomWrapper>
                    ) : previewUrl && selectedAtt.content_type === 'application/pdf' ? (
                      // Safari does not render PDFs inside <iframe> at all (renders
                      // blank regardless of sandboxing) — <object> is the tag its
                      // built-in viewer actually honors, and it's what every other
                      // engine supports too. The children below are the standard
                      // HTML fallback content, shown only if nothing could render.
                      // ZoomWrapper gives the plugin MORE room to render into when
                      // zoomed out (not just a shrunk copy of its own crop) — the
                      // native viewer's own default zoom otherwise leaves Safari
                      // users stuck scrolling a page that doesn't fit the panel.
                      <ZoomWrapper zoom={zoom}>
                        <Box component="object" data={previewUrl} type="application/pdf" sx={{ width: '100%', height: '100%' }}>
                          <Box sx={{ p: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                              Your browser can't display this PDF inline.
                            </Typography>
                            <Button size="small" sx={{ mt: 1 }} startIcon={<OpenInNewIcon />} onClick={() => handleOpen(selectedAtt)}>
                              Open in new tab
                            </Button>
                          </Box>
                        </Box>
                      </ZoomWrapper>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
                        No preview available for this file type.
                      </Typography>
                    )}
                    {(previewText !== null || previewUrl) && !previewLoading && !previewError && (
                      <Box
                        sx={{
                          position: 'absolute', bottom: 8, right: 8,
                          display: 'flex', alignItems: 'center', gap: 0.25,
                          bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                          borderRadius: 1, boxShadow: 2, px: 0.25,
                        }}
                      >
                        <Tooltip title="Zoom out">
                          <span>
                            <IconButton size="small" onClick={zoomOut} disabled={zoom <= ZOOM_MIN}>
                              <ZoomOutIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Reset zoom">
                          <Typography
                            variant="caption"
                            sx={{ minWidth: 36, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }}
                            onClick={zoomReset}
                          >
                            {Math.round(zoom * 100)}%
                          </Typography>
                        </Tooltip>
                        <Tooltip title="Zoom in">
                          <span>
                            <IconButton size="small" onClick={zoomIn} disabled={zoom >= ZOOM_MAX}>
                              <ZoomInIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}

        {/* OCR text preview */}
        {ocrTextId && (
          <Box sx={{ m: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, maxHeight: 150, overflow: 'auto' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              OCR extracted text:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 11, whiteSpace: 'pre-wrap' }}>
              {attachments.find((a) => a.id === ocrTextId)?.ocr_text || 'No text extracted'}
            </Typography>
          </Box>
        )}

      {onApplyExtraction && (
        <FillFromFileDialog
          open={fillOpen}
          onClose={() => setFillOpen(false)}
          fields={fillFields}
          tabularParts={fillParts}
          proposal={fillProposal}
          currentData={currentData || {}}
          onApply={onApplyExtraction}
        />
      )}

      {/* Cloud settings dialog */}
      <CloudSettingsDialog
        open={cloudSettingsOpen}
        onClose={() => setCloudSettingsOpen(false)}
        connectionId={connectionId}
        settings={cloudSettings}
        onSaved={(s) => setCloudSettings(s)}
      />

      {/* Link dialog */}
      <Dialog open={linkDialogOpen} onClose={() => setLinkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Link</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}>
          <TextField
            label="URL *"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            size="small"
            fullWidth
            autoFocus
            placeholder="https://drive.google.com/file/d/..."
          />
          <TextField
            label="Display name"
            value={linkName}
            onChange={(e) => setLinkName(e.target.value)}
            size="small"
            fullWidth
            placeholder="Contract.pdf"
            helperText="Optional — extracted from URL if empty"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddLink} disabled={!linkUrl.trim() || uploading}>
            {uploading ? 'Adding...' : 'Add Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )

  // Embedded mode: no header, no border — used inside a tab
  if (embedded) {
    // flex-fill: the Files tab wrapper in RuntimeFormRenderer stretches to
    // the bottom of the form, and this is what lets the preview pane below
    // grow into that space instead of sitting at a fixed height with dead
    // space underneath it.
    return <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'auto' }}>{bodyContent}</Box>
  }

  return (
    <Box sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
          bgcolor: 'action.hover', cursor: 'pointer',
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <AttachFileIcon sx={{ fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          Attached files ({attachments.length})
        </Typography>
        {loading && <CircularProgress size={14} />}
        {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
      </Box>
      <Collapse in={expanded}>
        {bodyContent}
      </Collapse>
    </Box>
  )
}

/** Dialog for configuring cloud provider API keys — tabbed per provider */
function CloudSettingsDialog({
  open, onClose, connectionId, settings, onSaved,
}: {
  open: boolean
  onClose: () => void
  connectionId: string
  settings: import('./cloudPickers').CloudSettings
  onSaved: (s: import('./cloudPickers').CloudSettings) => void
}) {
  const [form, setForm] = useState({ ...settings })
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState(0)

  useEffect(() => { setForm({ ...settings }) }, [settings, open])

  const handleSave = async () => {
    setSaving(true)
    try {
      const { saveCloudSettings } = await import('./cloudPickers')
      const result = await saveCloudSettings(connectionId, form)
      onSaved(result)
      onClose()
    } catch { /* ignore */ }
    setSaving(false)
  }

  const isGoogleConfigured = Boolean(form.google_picker_api_key && form.google_picker_client_id)
  const isOneDriveConfigured = Boolean(form.onedrive_client_id)
  const isDropboxConfigured = Boolean(form.dropbox_app_key)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudIcon color="primary" />
        Cloud Storage
      </DialogTitle>
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ px: 2, borderBottom: 1, borderColor: 'divider', minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0.5 } }}
      >
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Google Drive
            {isGoogleConfigured && <Chip component="span" label="ON" size="small" color="success" sx={{ height: 16, fontSize: 9 }} />}
          </Box>
        } />
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            OneDrive
            {isOneDriveConfigured && <Chip component="span" label="ON" size="small" color="success" sx={{ height: 16, fontSize: 9 }} />}
          </Box>
        } />
        <Tab label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Dropbox
            {isDropboxConfigured && <Chip component="span" label="ON" size="small" color="success" sx={{ height: 16, fontSize: 9 }} />}
          </Box>
        } />
      </Tabs>
      <DialogContent sx={{ minHeight: 220 }}>
        {tab === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: 12 } }}>
              Create credentials at <Typography component="a" variant="body2" href="https://console.cloud.google.com/" target="_blank" rel="noopener" sx={{ fontWeight: 600 }}>Google Cloud Console</Typography>.
              Enable the <b>Google Picker API</b>, then create an API Key and an OAuth 2.0 Client ID.
            </Alert>
            <TextField
              label="API Key"
              size="small"
              fullWidth
              value={form.google_picker_api_key || ''}
              onChange={(e) => setForm(f => ({ ...f, google_picker_api_key: e.target.value }))}
              placeholder="AIzaSy..."
            />
            <TextField
              label="OAuth Client ID"
              size="small"
              fullWidth
              value={form.google_picker_client_id || ''}
              onChange={(e) => setForm(f => ({ ...f, google_picker_client_id: e.target.value }))}
              placeholder="123456789.apps.googleusercontent.com"
            />
          </Box>
        )}
        {tab === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: 12 } }}>
              Register an app at <Typography component="a" variant="body2" href="https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationsListBlade" target="_blank" rel="noopener" sx={{ fontWeight: 600 }}>Azure Portal → App registrations</Typography>.
              Copy the <b>Application (client) ID</b>. Set redirect URI to <code>{typeof window !== 'undefined' ? window.location.origin : ''}</code>.
            </Alert>
            <TextField
              label="Application (Client) ID"
              size="small"
              fullWidth
              value={form.onedrive_client_id || ''}
              onChange={(e) => setForm(f => ({ ...f, onedrive_client_id: e.target.value }))}
              placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            />
          </Box>
        )}
        {tab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Alert severity="info" sx={{ '& .MuiAlert-message': { fontSize: 12 } }}>
              Create an app at <Typography component="a" variant="body2" href="https://www.dropbox.com/developers/apps" target="_blank" rel="noopener" sx={{ fontWeight: 600 }}>Dropbox App Console</Typography>.
              Choose <b>Scoped access</b> → <b>Full Dropbox</b>. Copy the <b>App key</b>.
            </Alert>
            <TextField
              label="App Key"
              size="small"
              fullWidth
              value={form.dropbox_app_key || ''}
              onChange={(e) => setForm(f => ({ ...f, dropbox_app_key: e.target.value }))}
              placeholder="xxxxxxxxxx"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
